<script setup lang="ts">
import type { LinkTarget } from '~/enums/link-target'

const router = useRouter()

const { isLoggedIn, dashboardUrl, fullName } = useDashboardAware()

router.beforeEach(() => {
  currentEntity.value = undefined
})

const currentEntity = useCurrentEntity()

const entityUrl = computed<string | undefined>(() => {
  if (!currentEntity.value || !dashboardUrl.value) {
    return undefined
  }

  return `${dashboardUrl.value}${currentEntity.value.dashboardUrl}`
})

const entityTitle = computed<string | undefined>(() => currentEntity.value?.title)
const message = computed<string | undefined>(() => currentEntity.value?.message)

const setBodyClass = (hasAdminBar: boolean) => {
  if (!document) {
    return
  }

  const htmlRoot = document.getElementsByTagName('html')[0]
  htmlRoot.classList[hasAdminBar ? 'add' : 'remove']('has-adminbar')
}

watch(
  () => isLoggedIn.value,
  newValue => setBodyClass(newValue),
)

onMounted(() => {
  setBodyClass(isLoggedIn.value)
})
</script>

<template>
  <section
    v-if="isLoggedIn"
    class="adminbar"
  >
    <div class="adminbar__root">
      <ul class="adminbar__root-default">
        <li
          v-if="dashboardUrl"
          class="adminbar__item"
        >
          <UIButton
            :href="dashboardUrl"
            appended-icon="fluent:top-speed-20-filled"
            variant="clear-light"
            :target="LinkTarget.BLANK"
            size="sm"
            class="adminbar__link"
          >
            Dashboard
          </UIButton>
        </li>

        <li
          v-if="entityUrl"
          class="adminbar__item"
        >
          <UIButton
            :href="entityUrl"
            appended-icon="fluent:edit-48-filled"
            :target="LinkTarget.BLANK"
            variant="clear-light"
            rel="nofollow"
            size="sm"
            class="adminbar__link"
          >
            Edit {{ entityTitle }}
          </UIButton>
        </li>
      </ul>
      <span
        v-if="message"
        class="adminbar__message"
      >
        <Icon
          name="fluent:warning-20-filled"
          class="adminbar__icon"
        />
        {{ message }}
      </span>
      <ul class="adminbar__top-secondary">
        <li
          v-if="isLoggedIn"
          class="adminbar__item adminbar__item--account"
        >
          <UIButton
            v-if="dashboardUrl"
            :href="dashboardUrl"
            variant="clear-light"
            :target="LinkTarget.BLANK"
            class="adminbar__link"
            size="sm"
          >
            Welcome, {{ fullName }}
          </UIButton>
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped lang="scss">
.adminbar {
  @apply w-full fixed bottom-0 bg-gray-800 text-gray-400 z-[10000] text-sm print:hidden;

  &__root {
    @apply flex items-center justify-between px-[60px];
    @apply md:pl-[90px];
  }

  &__top-secondary {
    @apply hidden md:inline-block;
  }

  &__root-default {
    @apply flex items-center;
  }

  &__link {
    @apply flex items-center text-white/30 normal-case text-[14px];
    .iconify {
      @apply w-5 h-5 fill-current mr-2;
    }

    &:hover,
    &:focus-within {
      @apply text-white;
    }
  }

  &__item {
    @apply relative px-0 min-h-[40px] flex mr-[20px];

    &--account {
      @apply mr-0;
    }
  }

  &__edit {
    @apply inline-block;
  }

  &__message {
    @apply flex whitespace-nowrap overflow-ellipsis;
  }

  &__icon {
    @apply h-5 w-5 mr-2;
  }
}
</style>
